/**
 * Input component
 *
 * A styled `<input>` wrapper that follows the design-system ring/focus
 * conventions and supports leading/trailing addons (icon slots).
 *
 * The component is intentionally thin — it does not include label or error
 * state.  Compose it with Radix Label and a `<p>` for accessible form fields.
 *
 * @example Plain
 *   <Input type="email" placeholder="you@example.com" />
 *
 * @example With left icon
 *   <Input leftAddon={<Search className="size-4 text-muted-foreground" />} />
 *
 * @example Controlled
 *   const [value, setValue] = useState("");
 *   <Input value={value} onChange={(e) => setValue(e.target.value)} />
 */

import * as React from "react";

import { cn } from "../utils/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Node rendered inside the input container to the left of the `<input>`
   * element.  The container handles positioning — just pass an icon.
   */
  leftAddon?: React.ReactNode;
  /**
   * Node rendered inside the input container to the right of the `<input>`
   * element.  Useful for clear buttons, password-toggle icons, etc.
   */
  rightAddon?: React.ReactNode;
  /**
   * When true, renders an error ring instead of the default focus ring.
   * Wire this to your form validation state.
   */
  hasError?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", leftAddon, rightAddon, hasError, ...props }, ref) => {
    const hasAddon = leftAddon !== undefined || rightAddon !== undefined;

    if (!hasAddon) {
      return (
        <input
          type={type}
          ref={ref}
          className={cn(
            // Layout
            "flex h-9 w-full rounded-md",
            // Colours
            "border border-input bg-transparent px-3 py-1",
            // Typography
            "text-sm shadow-sm",
            // Placeholder
            "placeholder:text-muted-foreground",
            // Focus
            "focus-visible:outline-none focus-visible:ring-1",
            hasError
              ? "border-destructive focus-visible:ring-destructive"
              : "focus-visible:ring-ring",
            // Disabled
            "disabled:cursor-not-allowed disabled:opacity-50",
            // File input reset
            "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
            className,
          )}
          aria-invalid={hasError}
          {...props}
        />
      );
    }

    // Addon wrapper — positions icon nodes inside the visual input box
    return (
      <div
        className={cn(
          "flex h-9 items-center rounded-md border border-input bg-transparent shadow-sm",
          "focus-within:ring-1",
          hasError
            ? "border-destructive focus-within:ring-destructive"
            : "focus-within:ring-ring",
          "has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50",
        )}
      >
        {leftAddon && (
          <span className="flex shrink-0 items-center pl-3" aria-hidden="true">
            {leftAddon}
          </span>
        )}
        <input
          type={type}
          ref={ref}
          className={cn(
            "flex-1 bg-transparent px-3 py-1 text-sm placeholder:text-muted-foreground",
            "focus-visible:outline-none",
            "disabled:cursor-not-allowed",
            className,
          )}
          aria-invalid={hasError}
          {...props}
        />
        {rightAddon && (
          <span className="flex shrink-0 items-center pr-3" aria-hidden="true">
            {rightAddon}
          </span>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { Input };
